/**
 * OrganizationMetricsSection — the admin "Metrics" tab for one organization.
 *
 * Renders the EXACT practice-metrics grid the org's practice owner sees, scoped to
 * this org via the admin container route, by reusing the real SDUI rendering path
 * (ServerUiDomainProvider → ComponentSlot → SemanticMetricTile). It deliberately
 * drops the provider chrome — no /metrics drill-in links, no Statsig tracking, no
 * "Improve this → book a call" optimization modal. Clicking a tile opens the
 * org-scoped drill-in; the lever becomes "Add intervention marker".
 *
 * Admin tooling is Statsig-exempt (see web-app CLAUDE.md), so no event tracking here.
 */
import React, { useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import {
  ComponentSlot,
  type ServerUiComponent,
  ServerUiDomainProvider,
} from '@features/server-ui';
import { TileSkeleton } from '@features/semantic-layer/components/TileSkeleton';
import { semanticDomainComponents } from '@features/semantic-layer/registry';
import { SentryErrorBoundary } from '@services/SentryService';

import { useAdminSemanticContainerQuery } from '../store/hooks';

import { AddInterventionDialog } from './add-intervention-dialog';
import { AdminMetricDetailDialog } from './admin-metric-detail-dialog';

// Reserve each lazy tile's height before it resolves — no layout shift on load.
const SEMANTIC_TILE_SKELETONS = { byPrimitive: { domain_payload: TileSkeleton } };

const tileSlug = (c: ServerUiComponent): string | undefined =>
  (c.placeholder as { slug?: string } | null)?.slug;

/** The grid, reusing the SDUI dispatch path but with an admin click handler. */
const AdminSemanticGrid: React.FC<{
  components: ServerUiComponent[];
  organizationId: string;
  onTileClick: (slug: string) => void;
}> = ({ components, organizationId, onTileClick }) => (
  <ServerUiDomainProvider
    components={semanticDomainComponents}
    skeletons={SEMANTIC_TILE_SKELETONS}
    containerKey="semantic_metrics"
    entityId={organizationId}
  >
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
        gap: 2,
        alignItems: 'stretch',
      }}
    >
      {components.map((c, i) => {
        const slug = tileSlug(c);
        const tile = (
          <SentryErrorBoundary>
            <ComponentSlot component={c} />
          </SentryErrorBoundary>
        );
        return slug ? (
          <Box
            key={slug}
            onClick={() => onTileClick(slug)}
            sx={{
              cursor: 'pointer',
              borderRadius: 2.5,
              transition: 'transform 120ms ease, box-shadow 120ms ease',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
            }}
          >
            {tile}
          </Box>
        ) : (
          <Box key={`tile-${i}`}>{tile}</Box>
        );
      })}
    </Box>
  </ServerUiDomainProvider>
);

export const OrganizationMetricsSection: React.FC<{ organizationId: string }> = ({
  organizationId,
}) => {
  const { data, isLoading, isError, refetch } = useAdminSemanticContainerQuery(
    { organizationId, windowDays: 90 },
    { skip: !organizationId },
  );

  const [detailSlug, setDetailSlug] = useState<string | null>(null);
  const [interventionFor, setInterventionFor] = useState<{
    slug?: string;
    name?: string;
  } | null>(null);

  const components = ((data?.sections ?? []).flatMap(
    s => s.components,
  ) as unknown) as ServerUiComponent[];

  return (
    <Box>
      <Stack
        direction="row"
        sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Practice metrics
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The grid this organization&apos;s practice owner sees. Click a tile to drill
            in; add intervention markers to annotate template / rule / schedule changes.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setInterventionFor({})}
        >
          Add intervention
        </Button>
      </Stack>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}
      {isError && !isLoading && (
        <Alert severity="error">Couldn&apos;t load metrics for this organization.</Alert>
      )}
      {!isLoading && !isError && components.length === 0 && (
        <Alert severity="info">
          No metrics to show yet. The practice-metrics catalog may not be loaded for this
          environment, or this organization has no audited notes in the trailing 90 days.
        </Alert>
      )}
      {!isLoading && !isError && components.length > 0 && (
        <AdminSemanticGrid
          components={components}
          organizationId={organizationId}
          onTileClick={setDetailSlug}
        />
      )}

      <AdminMetricDetailDialog
        open={!!detailSlug}
        slug={detailSlug}
        organizationId={organizationId}
        onClose={() => setDetailSlug(null)}
        onAddIntervention={(slug, name) => setInterventionFor({ slug, name })}
      />

      <AddInterventionDialog
        open={!!interventionFor}
        organizationId={organizationId}
        metricSlug={interventionFor?.slug}
        metricName={interventionFor?.name}
        onClose={() => setInterventionFor(null)}
        onSuccess={() => refetch()}
      />
    </Box>
  );
};

export default OrganizationMetricsSection;
